import ModalFooter from "features/shared/components/modal/ModalFooter";
import ModalHeader from "features/shared/components/modal/ModalHeader";
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "features/shared/forms/hooks/useForm";
import FormGroup from "features/shared/forms/components/FormGroup";
import { bankAccountValidations, validatePreAccountNumber, validateAccountNumber, validateIbanNumber, validateBankAccountNumber } from "./bankAccountValidations";
import CustomerBankAccountDTO from "features/customer/dtos/CustomerBankAccountDTO";
import BankTypeEnumDTO from "features/shared/enums/dynamic/BankTypeEnumDTO";
import Select from "react-select";
import reactSelectStyles from "features/shared/components/reactSelect/reactselectcustom.module.scss";
import { Nullable } from "features/shared/types/nullable";
import { useTranslation } from "react-i18next";

interface Props {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmit: (bankAccount: CustomerBankAccountDTO) => void;
  banks: BankTypeEnumDTO[];
}

const BankAccountDialog = ({ show, setShow, onSubmit, banks }: Props) => {
  // translations
  const tLabels = useTranslation("payments\\payments").t;  

  // state
  const [internationalAccount, setInternationalAccount] = useState(false);

  // form hook
  const {
    values,
    errors,
    bindField,
    setFormValue,
    validateFormField,
    setValidationError,
    clearForm,
  } = useForm({
    initialValidations: bankAccountValidations()
  });

  // helpers
  const hasError = (inputName: string): string | undefined => {
    return errors[inputName] && !internationalAccount ? errors[inputName] : undefined
  }

  const validateForm = (): boolean => {
    if (internationalAccount) {
      return validateIbanNumber(values["iban"], validateFormField, setValidationError);
    } else {
      return validateBankAccountNumber(values, validateFormField, setValidationError);
    }
  }

  const getBankAccount = (): CustomerBankAccountDTO => {
    let bankAccount: CustomerBankAccountDTO;

    if (internationalAccount) {
      let iban: string = values["iban"];
      bankAccount = {
        iban: iban,
        bankCode: iban.substr(4, 4), // 4 - lenght of bank code
        preAccountNumber: removeLeadingZeros(iban.substr(8, 6)), // 6 - lenght of pre-account number
        accountNumber: removeLeadingZeros(iban.substr(14, 10)) // 10 - lenght of account number
      };

      if (bankAccount.preAccountNumber === "") {
        delete bankAccount.preAccountNumber;
      }

    } else {
      bankAccount = { ...values };
      delete bankAccount.iban;
    }   

    return bankAccount;
  }

  const removeLeadingZeros = (number: string): string => number.replace(/^0+/g, "");

  // handlers
  const submitFormHandler = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      let bankAccount: CustomerBankAccountDTO = getBankAccount();
      onSubmit(bankAccount);
      setInternationalAccount(false);
      clearForm();
    }
  };

  const closeDialogHandler = () => {
    setShow(false);
    setInternationalAccount(false);
    clearForm();
  };

  const bankChangeHandler = (bank: Nullable<BankTypeEnumDTO>) => {
    if (validateFormField("bankCode", bank) && bank) {
      setFormValue("bankCode", bank.code);
    }
  };

  const preAccountNumberBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    validatePreAccountNumber(e.target.value, validateFormField, setValidationError);
  }

  const accountNumberBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    validateAccountNumber(e.target.value, validateFormField, setValidationError);
  }

  const ibanBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    validateIbanNumber(e.target.value, validateFormField, setValidationError);
  }

  // render
  return (
    <Modal show={show} onHide={closeDialogHandler}>
      <ModalHeader
        title={tLabels("bankAccountDialog.cardTitle")}
        onCloseClick={closeDialogHandler}
      />
      <form onSubmit={submitFormHandler}>
        <div className="modal-body py-4">
          <div className="row no-gutters mx-n2">
            <div className="col-sm-4 px-2">
              <FormGroup 
                labelText={tLabels("bankAccountDialog.preNumber")}
                validationError={hasError("preAccountNumber")}
              >
                <input
                  className="form-control"
                  placeholder={tLabels("bankAccountDialog.preAccountNumber")}
                  disabled={internationalAccount}
                  onBlur={preAccountNumberBlurHandler}
                  {...bindField("preAccountNumber")}
                />
              </FormGroup>
            </div>
            <div className="col-sm-8 px-2">
              <FormGroup 
                labelText={tLabels("bankAccountDialog.accountNumber")}
                validationError={hasError("accountNumber")}
              >
                <input
                  className="form-control"
                  placeholder={tLabels("bankAccountDialog.setAccountNumber")}
                  disabled={internationalAccount}
                  onBlur={accountNumberBlurHandler}
                  {...bindField("accountNumber")}
                />
              </FormGroup>
            </div>
          </div>
          <FormGroup
            labelText={tLabels("bankAccountDialog.bank")}
            validationError={hasError("bankCode")}
          >
            <Select
              options={banks}
              getOptionLabel={(bank) => `${bank.code} - ${bank.name}`}
              getOptionValue={(bank) => bank.code!}
              isDisabled={internationalAccount}
              onChange={bankChangeHandler}
              captureMenuScroll={false}
              className={reactSelectStyles.customSelect}
              classNamePrefix="customSelect"
              placeholder={tLabels("bankAccountDialog.setBank")}
              noOptionsMessage={() => tLabels("bankAccountDialog.noBanksFound")}
            />
          </FormGroup>
 
          <div className="custom-control custom-checkbox mt-4">
            <input
              type="checkbox"
              className="custom-control-input"
              id="InternationalAccount"
              checked={internationalAccount}
              onChange={(e) => setInternationalAccount(e.target.checked)}
            />
            <label className="custom-control-label" htmlFor="InternationalAccount">
              {tLabels("bankAccountDialog.setInternationalAccountNumber")}
            </label>
          </div>

          <FormGroup 
            labelText="IBAN" 
            className="mt-3"
            visible={internationalAccount}
            validationError={errors["iban"] ?? undefined}
          >
            <input
              className="form-control"
              onBlur={ibanBlurHandler}
              placeholder={tLabels("bankAccountDialog.setCzechOrSlovakIban")}
              {...bindField("iban")}
            />
          </FormGroup>
        </div>
        <ModalFooter>
          <button type="submit" className="btn btn-primary">
            {tLabels("bankAccountDialog.add")}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default BankAccountDialog;
